import { useState, useEffect } from 'react';
import {
  Button,
  Alert,
  PasswordField,
  TextField,
  FormField,
} from '@elementary/common-ui/components';
import FormControls from '@elementary/common-ui/components/FormControls';
import { Divider, Typography } from '@mui/material';
import useNango from 'shared/hooks/useNango';
import { fetchNangoInstalledApps } from 'shared/services/settingsApi';
import { AuthSectionProps } from './types';

const GitHubAuthSection = ({
  formMethods,
  requiredLabel,
}: AuthSectionProps) => {
  const {
    register,
    formState: { errors, dirtyFields },
    setValue,
  } = formMethods;
  const [githubAppConnected, setGithubAppConnected] = useState<boolean | null>(
    null,
  );
  const [loadingGithubAppStatus, setLoadingGithubAppStatus] = useState(false);
  const { nangoAuth, success: nangoSuccess, error: nangoError } = useNango();
  const nangoProviderId = 'github';

  useEffect(() => {
    setLoadingGithubAppStatus(true);
    fetchNangoInstalledApps()
      .then((res) => setGithubAppConnected(!!res.github))
      .finally(() => setLoadingGithubAppStatus(false));
  }, []);

  useEffect(() => {
    if (nangoSuccess) {
      setValue('lookerGitToken', '');
      setValue('lookerEnterpriseUrl', '');
    }
  }, [nangoSuccess, setValue]);

  if (loadingGithubAppStatus) {
    return (
      <Typography sx={{ color: 'gray' }}>
        Checking GitHub app connection...
      </Typography>
    );
  }

  return (
    <>
      <Button onClick={() => nangoAuth(nangoProviderId)} sx={{ mb: 2 }}>
        {githubAppConnected
          ? "Connect LookML repository with Elementary's Github app"
          : 'Connect Elementary GitHub App'}
      </Button>
      {nangoSuccess && (
        <Alert
          severity="success"
          style={{ fontWeight: 'bold', marginBottom: 8 }}
        >
          Elementary GitHub app was successfully connected
        </Alert>
      )}
      {nangoError && (
        <Alert severity="error" style={{ fontWeight: 'bold' }}>
          Elementary GitHub app failed to connect. Please try again or contact
          support.
        </Alert>
      )}
      <Divider>OR</Divider>
      <FormControls.InputWrapper>
        <PasswordField
          {...register('lookerGitToken', {
            required: !(githubAppConnected || nangoSuccess)
              ? requiredLabel
              : false,
          })}
          label="Token"
          isFormValueDirty={dirtyFields.lookerGitToken}
          clearFormValue={() => setValue('lookerGitToken', '')}
          disabled={!!nangoSuccess}
          size="large"
        />
        <FormControls.ErrorMessage>
          {errors.lookerGitToken?.message}
        </FormControls.ErrorMessage>
      </FormControls.InputWrapper>
      <FormField
        title="Enterprise URL"
        description="Leave empty to use the default GitHub URL"
        optional
      >
        <TextField
          {...register('lookerEnterpriseUrl')}
          variant="outlined"
          placeholder="https://hostname/api/v3"
          disabled={!!nangoSuccess}
          size="large"
        />
      </FormField>
    </>
  );
};

export default GitHubAuthSection;
